import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {CommonConfigurator} from '@spartacus/product-configurator/common';
import {
  Configurator,
  ConfiguratorAttributeCompositionContext,
  ConfiguratorAttributeInputFieldComponent,
  ConfiguratorCommonsService,
  ConfiguratorStorefrontUtilsService,
  ConfiguratorUISettingsConfig,
} from '@spartacus/product-configurator/rulebased';
import {take} from 'rxjs/operators';
import {Router} from '@angular/router';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {HasPendingChangesService} from '../disable-loading-form/has-pending-changes.service';
import {Observable} from 'rxjs';
import {LanguageService} from '@spartacus/core';
import moment from 'moment';
import token, {serviceProviderTokens} from '../../token/tokens';
import {DatePickerViewModel, FeatureToggleConfig} from '../../models';
import {FeatureTogglesService, MomentService} from '../../services';
import {IAuthService} from '../../interfaces';

@UntilDestroy()
@Component({
  selector: 'dcp-configurator-attribute-input-and-date-input-field',
  templateUrl: './dcp-configurator-attribute-input-and-date-input-field.component.html',
  styleUrl: './dcp-configurator-attribute-input-and-date-input-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DcpConfiguratorAttributeInputAndDateInputFieldComponent
  extends ConfiguratorAttributeInputFieldComponent
  implements OnInit
{
  public showErrorLocal: boolean = false;
  public dates: DatePickerViewModel = {};
  public dateFormat: string = 'dd-MMM-yyyy';
  public dateFormatISO: string = 'YYYY-MM-DD';
  activeLanguage$: Observable<string> = this.languageService.getActive();
  attributeDateForm = new UntypedFormControl('');
  endDate?: Configurator.Attribute;
  minDate: NgbDateStruct | undefined;
  maxDate: NgbDateStruct | undefined;

  constructor(
    public momentService: MomentService,
    @Inject(serviceProviderTokens.AUTH_SERVICE_PROVIDER)
    private authService: IAuthService,
    private router: Router,
    configuratorUISettingsConfig: ConfiguratorUISettingsConfig,
    protected override configuratorCommonsService: ConfiguratorCommonsService,
    public hasPendingChangesService: HasPendingChangesService,
    configuratorAttributeCompositionContext: ConfiguratorAttributeCompositionContext,
    protected languageService: LanguageService,
    configuratorStorefrontUtilsService: ConfiguratorStorefrontUtilsService,
    protected featureTogglesService: FeatureTogglesService,
    private cdr: ChangeDetectorRef,
    @Inject(token.values.FEATURE_TOGGLES) protected featureToggles: FeatureToggleConfig,
  ) {
    super(
      configuratorUISettingsConfig,
      configuratorAttributeCompositionContext,
      configuratorCommonsService,
      configuratorStorefrontUtilsService,
    );
    this.showErrorLocal = typeof configuratorUISettingsConfig.productConfigurator === 'undefined';
    this.attribute = this.attributeComponentContext.attribute;
  }

  get minDateString(): string {
    return this.minDate ? `${this.minDate.year}-${this.minDate.month}-${this.minDate.day}` : '';
  }

  get maxDateString(): string {
    return this.maxDate ? `${this.maxDate.year}-${this.maxDate.month}-${this.maxDate.day}` : '';
  }

  shouldBeReadOnly(): boolean {
    return (this.minDate && this.maxDate && this.isEqualDate(this.minDate, this.maxDate)) || false;
  }

  private isEqualDate(date1: NgbDateStruct, date2: NgbDateStruct): boolean {
    return date1.year === date2.year && date1.month === date2.month && date1.day === date2.day;
  }

  isStartDateAttribute(): boolean {
    return this.attribute?.name.includes('START_DATE');
  }

  override ngOnInit(): void {
    this.endDate = this.attributeComponentContext.group.attributes?.find((attr) => attr.name.endsWith('END_DATE_STO'));
    this.authService
      .isUserLoggedIn()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((isLoggedIn) => {
        if (!isLoggedIn) {
          this.authService.login(this.router.url);
        }
      });

    if (
      this.ownerType === CommonConfigurator.OwnerType.CART_ENTRY &&
      this.attribute.required &&
      this.attribute.incomplete &&
      !this.attributeDateForm.value
    ) {
      this.attributeDateForm.markAsTouched();
    }

    this.attributeDateForm.valueChanges.pipe(untilDestroyed(this)).subscribe((newValue) => {
      const formattedDate = newValue ? this.momentService.ngbDateToMoment(newValue).format('YYYY-MM-DD') : '';
      this.onSelect(formattedDate);
    });
    if (this.attribute.userInput !== undefined) {
      this.attributeDateForm.setValue(this.momentService.stringToNgbDateStruct(this.attribute.userInput), {
        emitEvent: false,
      });
    }
    this.getDateRange();
  }

  getDateRange() {
    this.dates = this.momentService.getStartDateRange(this.attribute?.entryFieldMask) as DatePickerViewModel;
    this.minDate = this.momentService.momentToNgbDateStruct(this.dates?.startDatePeriod?.startDate);
    this.maxDate = this.momentService.momentToNgbDateStruct(this.dates?.startDatePeriod?.endDate);
    this.cdr.detectChanges();
  }

  onSelect(value: string): void {
    this.configuratorCommonsService.updateConfiguration(
      this.ownerKey,
      {
        ...this.attribute,
        userInput: value,
      },
      Configurator.UpdateType.ATTRIBUTE,
    );
  }

  public onSelectionChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const value = moment(customEvent.detail.selectedValues[0]).format(this.dateFormatISO);
    this.onSelect(value);
  }

  public getFormattedMomentDate(): string {
    const value = this.attributeDateForm.value;
    return value ? this.momentService.ngbDateToMoment(value).format('DD-MMM-yyyy') : '';
  }

  getSupportingText(): string {
    const validation = this.attribute?.validation || [];
    if (validation?.length > 0) {
      const descriptionText = validation[0]?.descriptionText || validation[0]?.description;
      return descriptionText || '';
    }
    return '';
  }

  getValueState(): string {
    return this.getSupportingText() ? 'Error' : 'Standard';
  }
}
